/**
 * @file Hooks et utilitaires d'accessibilité partagés (widgets et site public).
 * @module packages.common.hooks.useAccessibility
 *
 * Regroupe des primitives réutilisables pour la conformité WCAG 2.1 AA :
 * navigation clavier dans les listes et grilles, piège de focus pour les
 * modals et popups, annonces aux lecteurs d'écran via région live ARIA, et
 * helpers de génération de labels accessibles.
 */

import {useCallback, useEffect, useRef} from 'react';

/**
 * Gère la navigation clavier dans une liste, une rangée ou une grille
 * d'éléments (flèches, Home, End, Enter, Espace) conforme WCAG 2.1.
 *
 * En orientation `grid`, ArrowUp/ArrowDown se déplacent de `columns` indices.
 * `wrap` contrôle le rebouclage en début/fin ; Enter/Espace déclenchent
 * `onSelect` sans changer l'index.
 *
 * @param {Object} options - Options de configuration
 * @param {number} options.itemCount - Nombre d'éléments dans la liste
 * @param {number} options.currentIndex - Index actuellement sélectionné
 * @param {Function} options.onIndexChange - Callback appelé lors du changement d'index
 * @param {Function} [options.onSelect] - Callback appelé lors de la sélection (Enter/Space)
 * @param {boolean} [options.wrap=true] - Boucler au début/fin de la liste
 * @param {'horizontal'|'vertical'|'grid'} [options.orientation='horizontal'] - Orientation de navigation
 * @param {number} [options.columns=1] - Nombre de colonnes (pour orientation 'grid')
 * @return {{handleKeyDown: Function}} `handleKeyDown` à brancher sur l'événement
 *     `onKeyDown` du conteneur ; il appelle `preventDefault` sur les touches gérées.
 *
 * @example
 * const {handleKeyDown} = useKeyboardNavigation({
 *   itemCount: 10,
 *   currentIndex: selectedIndex,
 *   onIndexChange: setSelectedIndex,
 *   onSelect: handleItemSelect,
 *   orientation: 'horizontal',
 * });
 */
export function useKeyboardNavigation({
  itemCount,
  currentIndex,
  onIndexChange,
  onSelect,
  wrap = true,
  orientation = 'horizontal',
  columns = 1,
}) {
  const handleKeyDown = useCallback((event) => {
    if (itemCount === 0) return;

    let newIndex = currentIndex;
    let handled = false;

    const isHorizontal = orientation === 'horizontal';
    const isGrid = orientation === 'grid';

    switch (event.key) {
      case 'ArrowLeft':
        if (isHorizontal || isGrid) {
          newIndex = currentIndex - 1;
          if (newIndex < 0) newIndex = wrap ? itemCount - 1 : 0;
          handled = true;
        }
        break;

      case 'ArrowRight':
        if (isHorizontal || isGrid) {
          newIndex = currentIndex + 1;
          if (newIndex >= itemCount) newIndex = wrap ? 0 : itemCount - 1;
          handled = true;
        }
        break;

      case 'ArrowUp':
        if (!isHorizontal) {
          if (isGrid) {
            newIndex = currentIndex - columns;
            if (newIndex < 0) {
              newIndex = wrap ? itemCount + newIndex : currentIndex;
            }
          } else {
            newIndex = currentIndex - 1;
            if (newIndex < 0) newIndex = wrap ? itemCount - 1 : 0;
          }
          handled = true;
        }
        break;

      case 'ArrowDown':
        if (!isHorizontal) {
          if (isGrid) {
            newIndex = currentIndex + columns;
            if (newIndex >= itemCount) {
              newIndex = wrap ? newIndex - itemCount : currentIndex;
            }
          } else {
            newIndex = currentIndex + 1;
            if (newIndex >= itemCount) newIndex = wrap ? 0 : itemCount - 1;
          }
          handled = true;
        }
        break;

      case 'Home':
        newIndex = 0;
        handled = true;
        break;

      case 'End':
        newIndex = itemCount - 1;
        handled = true;
        break;

      case 'Enter':
      case ' ':
        if (onSelect) {
          event.preventDefault();
          onSelect(currentIndex);
        }
        return;

      default:
        return;
    }

    if (handled) {
      event.preventDefault();
      if (newIndex !== currentIndex) {
        onIndexChange(newIndex);
      }
    }
  }, [itemCount, currentIndex, onIndexChange, onSelect, wrap, orientation, columns]);

  return {handleKeyDown};
}

/**
 * Confine le focus clavier à l'intérieur d'un conteneur (modal, popup).
 *
 * Tant que `isActive`, le Tab boucle entre le premier et le dernier élément
 * focusable du conteneur, le focus est placé sur le premier au montage, et
 * Escape déclenche `onEscape` si fourni. À la désactivation, le focus revient
 * sur l'élément actif avant l'activation si `restoreFocus`.
 *
 * @param {boolean} isActive - Si le piège de focus est actif
 * @param {Object} [options] - Options de configuration
 * @param {Function} [options.onEscape] - Callback appelé lors de l'appui sur Escape
 * @param {boolean} [options.restoreFocus=true] - Restaurer le focus précédent à la désactivation
 * @return {React.RefObject} Ref à attacher au conteneur à piéger
 *
 * @example
 * const containerRef = useFocusTrap(isModalOpen, {
 *   onEscape: closeModal,
 *   restoreFocus: true,
 * });
 *
 * return <div ref={containerRef}>...</div>;
 */
export function useFocusTrap(isActive, options = {}) {
  const {onEscape, restoreFocus = true} = options;
  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    // Mémorise la cible vers laquelle restaurer le focus à la fermeture
    previousActiveElement.current = document.activeElement;

    const container = containerRef.current;

    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ];

    const getFocusableElements = () =>
      Array.from(container.querySelectorAll(focusableSelectors.join(',')));

    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        onEscape();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = getFocusableElements();
        if (focusable.length === 0) return;

        const firstElement = focusable[0];
        const lastElement = focusable[focusable.length - 1];

        // Shift + Tab depuis le premier élément : aller au dernier
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        // Tab depuis le dernier élément : aller au premier
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);

      if (restoreFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive, onEscape, restoreFocus]);

  return containerRef;
}

/**
 * Annonce des messages aux lecteurs d'écran via une région live ARIA jetable.
 *
 * Chaque appel injecte un nœud visuellement masqué (`role="status"`) dans le
 * `body`, y écrit le message, puis le retire après 1 s. La technologie
 * d'assistance lit le contenu sans déplacer le focus ni perturber le DOM
 * visible.
 *
 * @return {Function} `announce(message, priority='polite')` — `priority`
 *     `'polite'` attend une pause de lecture, `'assertive'` interrompt.
 *
 * @example
 * const announce = useAnnounce();
 *
 * const handlePageChange = (page) => {
 *   setCurrentPage(page);
 *   announce(`Page ${page} sur ${totalPages}`);
 * };
 */
export function useAnnounce() {
  const announce = useCallback((message, priority = 'polite') => {
    const announcer = document.createElement('div');
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', priority);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    announcer.textContent = message;

    document.body.appendChild(announcer);

    // Laisse le temps à la techno d'assistance de lire avant de retirer le nœud
    setTimeout(() => {
      if (announcer.parentNode) {
        document.body.removeChild(announcer);
      }
    }, 1000);
  }, []);

  return announce;
}

/**
 * Noms complets des jours de la semaine en français
 * Utilisés pour les labels accessibles des calendriers
 */
export const WEEKDAY_LABELS = [
  {abbr: 'L', full: 'Lundi'},
  {abbr: 'M', full: 'Mardi'},
  {abbr: 'M', full: 'Mercredi'},
  {abbr: 'J', full: 'Jeudi'},
  {abbr: 'V', full: 'Vendredi'},
  {abbr: 'S', full: 'Samedi'},
  {abbr: 'D', full: 'Dimanche'},
];

/**
 * Génère un label accessible pour une date de calendrier
 *
 * @param {Date} date - La date à décrire
 * @param {Object} [options] - Options de formatage
 * @param {boolean} [options.isSelected] - La date est sélectionnée
 * @param {boolean} [options.isToday] - La date est aujourd'hui
 * @param {boolean} [options.isAvailable] - La date est disponible
 * @param {boolean} [options.isWaitlist] - Liste d'attente disponible
 * @return {string} Label accessible pour la date
 *
 * @example
 * const label = getDateAriaLabel(date, {
 *   isSelected: true,
 *   isToday: false,
 *   isAvailable: true,
 * });
 * // "Lundi 15 janvier 2024, sélectionné, disponible"
 */
export function getDateAriaLabel(date, options = {}) {
  const {isSelected, isToday, isAvailable, isWaitlist} = options;

  const formatter = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const parts = [formatter.format(date)];

  if (isToday) {
    parts.push('aujourd\'hui');
  }

  if (isSelected) {
    parts.push('sélectionné');
  }

  if (isAvailable) {
    parts.push('disponible');
  } else if (isWaitlist) {
    parts.push('liste d\'attente disponible');
  } else {
    parts.push('non disponible');
  }

  return parts.join(', ');
}

/**
 * CSS pour masquer visuellement tout en restant accessible aux lecteurs d'écran
 * À utiliser pour les textes destinés uniquement aux technologies d'assistance
 */
export const visuallyHiddenStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Génère les props d'accessibilité pour un élément de liste sélectionnable
 *
 * @param {number} index - Index de l'élément
 * @param {number} selectedIndex - Index de l'élément sélectionné
 * @param {string} label - Label de l'élément
 * @return {Object} Props ARIA pour l'élément
 */
export function getSelectableItemProps(index, selectedIndex, label) {
  const isSelected = index === selectedIndex;
  return {
    'role': 'option',
    'aria-selected': isSelected,
    'aria-label': label,
    'tabIndex': isSelected ? 0 : -1,
  };
}
